import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader } from 'rsuite';

import FileTextIcon from '@rsuite/icons/legacy/FileText';
import ListIcon from '@rsuite/icons/List';
import PlusIcon from '@rsuite/icons/Plus';
import PeoplesIcon from '@rsuite/icons/Peoples';
import CalculatorIcon from '@rsuite/icons/legacy/Calculator';
import WarningIcon from '@rsuite/icons/legacy/Warning';
import TruckIcon from '@rsuite/icons/legacy/Truck';
import SearchIcon from '@rsuite/icons/Search';
import WechatIcon from '@rsuite/icons/Wechat';
import StarIcon from '@rsuite/icons/Star';
import InfoIcon from '@rsuite/icons/legacy/Info';
import NoticeIcon from '@rsuite/icons/Notice';
import MemberIcon from '@rsuite/icons/Member';

import { navSections } from '../../config/navSections';

import './HomePage.css';
import InstallAppButton from '../../components/InstallAppButton';
import NotificationPermission from '../../components/NotificationPermission/NotificationPermission';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';
import {
    subscribeToPush,
    unsubscribeFromPush,
} from '../../utils/pushSubscription';
import { useMessenger } from '../../context/messengerContext';
import { getContractsByID, getProfile } from '../../api/user';

const NAV_ICONS = {
    FileText: FileTextIcon,
    List: ListIcon,
    Plus: PlusIcon,
    Peoples: PeoplesIcon,
    Calculator: CalculatorIcon,
    Warning: WarningIcon,
    Truck: TruckIcon,
    Search: SearchIcon,
    Wechat: WechatIcon,
    Star: StarIcon,
    Info: InfoIcon,
    NoticeIcon,
    Member: MemberIcon,
};

function HomeCard({
    path,
    titleKey,
    icon,
    topLeftComponent,
    topRightComponent,
    bg,
    onClick,
}) {
    const { t } = useTranslation('basic');
    const IconFromConfig = NAV_ICONS[icon];
    const left =
        topLeftComponent ??
        (IconFromConfig ? (
            <IconFromConfig style={{ width: 24, height: 24 }} />
        ) : null);

    return (
        <button
            type='button'
            className={`homePage_card ${bg ?? ''}`.trim()}
            onClick={() => onClick(path)}
        >
            <div className='homePage_cardTop'>
                {left && <span className='homePage_cardTopLeft'>{left}</span>}
                {topRightComponent && (
                    <span className='homePage_cardTopRight'>
                        {topRightComponent}
                    </span>
                )}
            </div>
            <span className='homePage_cardTitle paragraph-16M'>
                {t(titleKey)}
            </span>
        </button>
    );
}

export default function HomePage() {
    const { t } = useTranslation('basic');
    const navigate = useNavigate();
    const { platform, userId } = useMessenger();
    const [isContractsLoading, setIsContractsLoading] = useState(true);

    const [isSomeAdmin, setIsSomeAdmin] = useState(false);

    const isIOS26Version = isIOSVersionHigherThan26Version();

    useEffect(() => {
        unsubscribeFromPush().then(() => subscribeToPush(platform, userId));
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadContracts = async () => {
            setIsContractsLoading(true);
            const MAX_ATTEMPTS = 4; // 1 начальный запрос + 3 перезапроса
            const DELAY = 500;

            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                    const contracts = await getContractsByID({ platform, userId });

                    if (cancelled) return;

                    // Если получили массив с договорами — всё успешно
                    if (Array.isArray(contracts) && contracts.length > 0) {
                        setIsContractsLoading(false);
                        return;
                    }

                    // Если это была последняя попытка и договоров всё еще нет (или пришла ошибка)
                    if (attempt === MAX_ATTEMPTS) {
                        navigate('/auth', { replace: true });
                        return;
                    }
                } catch (error) {
                    if (cancelled) return;
                    console.error(`Attempt ${attempt} failed to load contracts`, error);
                    
                    if (attempt === MAX_ATTEMPTS) {
                        setIsContractsLoading(false);
                        navigate('/auth', { replace: true });
                        return;
                    }
                }

                // Ждем 1 секунду перед следующим перезапросом, если попытка не последняя
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((resolve) => setTimeout(resolve, DELAY));
                }
            }
        };

        const loadProfile = async () => {
            try {
                const profile = await getProfile({ platform, userId });
                if (cancelled) return;
                const isAdminOnAnyContract = (
                    Array.isArray(profile?.employees) ? profile.employees : []
                ).some(
                    (e) =>
                        e?.is_admin === true &&
                        Boolean(e?.client?.contract_number),
                );
                setIsSomeAdmin(isAdminOnAnyContract);
            } catch (error) {
                if (cancelled) return;
                setIsSomeAdmin(false);
            }
        };

        loadContracts();
        loadProfile();

        return () => {
            cancelled = true;
        };
    }, [navigate, platform, userId]);

    if (isContractsLoading) {
        return (
            <div
                className={`loader-container ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
            >
                <Loader size='lg' />
            </div>
        );
    }

    function checkIOSAndPWA() {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isPWA =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

        if (isIOS && !isPWA) {
            console.log(
                'для работы push уведомлений установите приложение на рабочий стол',
            );
        }

        return isIOS && isPWA;
    }

    return (
        <div
            className={`homePage ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
        >
            <div className='homePage_header'>
                <h1 className='homePage_title heading-h4'>{t('home_title')}</h1>
            </div>
            <div className='homePage_grid'>
                {navSections.map((section) => {
                    if (section.path === '/employee_list' && !isSomeAdmin)
                        return null;

                    // if (isInvitedUser && section.path === '/employee_list') return null;

                    return (
                        <HomeCard
                            key={section.path}
                            path={section.path}
                            titleKey={section.titleKey}
                            icon={section.icon}
                            topLeftComponent={section.topLeftComponent}
                            topRightComponent={section.topRightComponent}
                            bg={section.bg}
                            onClick={navigate}
                        />
                    );
                })}
            </div>
            {/* Push Manager: {n} */}
            <div className='homePage_install'>
                <InstallAppButton />
            </div>
        </div>
    );
}
